import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, MessageCircle } from "lucide-react";
import TikTokIcon from "@/components/TikTokIcon";

const LOGO_URL = "/logo.webp";
const WHATSAPP = (process.env.REACT_APP_WHATSAPP_NUMBER || "27721960889").replace(/[^0-9]/g, "");

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-[#262626] mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <img src={LOGO_URL} alt="Untamed Gear" className="h-14 w-auto mb-4" />
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              Bold South African sportswear plus trusted sports equipment for rugby, hockey and netball. Custom, durable gear with unique designs.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-[0.2em] text-sm mb-4 font-heading">Quick Links</h4>
            <div className="flex flex-col gap-2">
              <Link to="/" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Home</Link>
              <Link to="/shop" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Shop</Link>
              <Link to="/shop?specials=true" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Specials</Link>
              <Link to="/gallery" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Gallery</Link>
              <Link to="/blog" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Blog</Link>
              <Link to="/policies" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Policies, Shipping & FAQ</Link>
              <Link to="/contact" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Contact</Link>
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-[0.2em] text-sm mb-4 font-heading">Programs</h4>
            <div className="flex flex-col gap-2">
              <Link to="/bulk-orders" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Team Kits & Bulk Orders</Link>
              <Link to="/ambassador" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Become an Ambassador</Link>
              <Link to="/ambassador-dashboard" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Ambassador Dashboard</Link>
              <Link to="/affiliate" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Affiliate Program</Link>
              <Link to="/affiliate-dashboard" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Affiliate Dashboard</Link>
            </div>
            <h4 className="text-white font-bold uppercase tracking-[0.2em] text-sm mb-3 mt-6 font-heading">Categories</h4>
            <div className="flex flex-col gap-2">
              <Link to="/shop/rugby" data-testid="footer-cat-rugby" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Rugby</Link>
              <Link to="/shop/hockey" data-testid="footer-cat-hockey" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Hockey</Link>
              <Link to="/shop/netball" data-testid="footer-cat-netball" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Netball</Link>
              <Link to="/shop/training" data-testid="footer-cat-training" className="text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">Training &amp; Equipment</Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-[0.2em] text-sm mb-4 font-heading">Contact</h4>
            <div className="flex flex-col gap-3">
              <a href="mailto:info@untamedsportsgear.co.za" className="flex items-center gap-2 text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">
                <Mail size={14} /> info@untamedsportsgear.co.za
              </a>
              <a href="tel:+27721960889" className="flex items-center gap-2 text-[#A3A3A3] text-sm hover:text-[#F7931E] transition-colors">
                <Phone size={14} /> Call: +27 72 196 0889
              </a>
              <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer"
                data-testid="footer-whatsapp" className="flex items-center gap-2 text-[#A3A3A3] text-sm hover:text-[#25D366] transition-colors">
                <MessageCircle size={14} /> WhatsApp: +27 72 196 0889
              </a>
              <div className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                <MapPin size={14} /> South Africa
              </div>
              <div className="flex gap-3 mt-2">
                <a href="https://www.instagram.com/untamedsportsgear" target="_blank" rel="noopener noreferrer"
                  data-testid="footer-instagram" className="text-[#A3A3A3] hover:text-[#F7931E] transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="https://www.facebook.com/share/1GHfSftdJ4/" target="_blank" rel="noopener noreferrer"
                  data-testid="footer-facebook" className="text-[#A3A3A3] hover:text-[#F7931E] transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="https://www.tiktok.com/@untamed.gear" target="_blank" rel="noopener noreferrer"
                  data-testid="footer-tiktok" className="text-[#A3A3A3] hover:text-[#F7931E] transition-colors">
                  <TikTokIcon size={20} />
                </a>
                <a href="https://www.youtube.com/@Untamedsportsgear" target="_blank" rel="noopener noreferrer"
                  data-testid="footer-youtube" className="text-[#A3A3A3] hover:text-[#F7931E] transition-colors">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-[#262626] mt-8 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[#525252] text-xs">
            &copy; {new Date().getFullYear()} Untamed Gear. All rights reserved.
          </p>
          <p className="text-[#525252] text-xs">
            Free shipping on orders above R1,000
          </p>
        </div>
      </div>
    </footer>
  );
}
